'use client';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useEffect, useRef, useState } from 'react';
import { PiShoppingCart } from 'react-icons/pi';

export type CardCartProps = { product: ProductDisplayData; sellable: boolean; className?: string };
type Runtime = typeof import('./cart-button-runtime').CardCartRuntime;
let runtimePromise: Promise<Runtime> | undefined;

export function loadCardCartRuntime() {
  return runtimePromise ??= import('./cart-button-runtime')
    .then(module => module.CardCartRuntime)
    .catch(error => { runtimePromise = undefined; throw error; });
}

export function DeferredCardCartButton(props: CardCartProps) {
  const { t } = useTranslations();
  const initialized = useCustomerStore(state => state.isInitialized);
  const [Runtime, setRuntime] = useState<Runtime>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const pendingClick = useRef(false);
  const restoreFocus = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  function activate(click = false) {
    // Warm code on intent, but never replace the pressed/focused DOM node before its click.
    if (!click) { void loadCardCartRuntime().catch(() => {}); return; }
    pendingClick.current ||= click;
    restoreFocus.current ||= !!host.current?.contains(document.activeElement);
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    loadCardCartRuntime().then(component => {
      if (mounted.current) setRuntime(() => component);
    }).catch(() => {
      pendingClick.current = false;
      if (mounted.current) setFailed(true);
    }).finally(() => {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    });
  }

  useEffect(() => {
    if (!Runtime) return;
    const button = host.current?.querySelector<HTMLButtonElement>('button');
    // Consume before dispatch: StrictMode/effect retries must not add a second item.
    const click = pendingClick.current;
    pendingClick.current = false;
    if (restoreFocus.current) button?.focus({ preventScroll: true });
    if (click && button && !button.disabled) button.click();
  }, [Runtime]);

  const supplier = [...(props.product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  // Affiliate links need no cart runtime at all; preserve their normal browser navigation.
  if (supplier?.is_affiliate) return (
    <Button asChild className={props.className}>
      <a href={supplier.url ?? '#'}><PiShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />{t('text1226')}</a>
    </Button>
  );
  return (
    <span ref={host} className="contents">
      {Runtime ? <Runtime {...props} /> : (
        <Button
          type="button"
          className={props.className}
          disabled={!initialized || props.product.status === 'ended' || !props.sellable}
          aria-label={t('text227')}
          aria-busy={loading}
          onPointerEnter={() => activate()}
          onFocus={() => activate()}
          onPointerDown={() => activate()}
          onClick={event => { event.preventDefault(); event.stopPropagation(); activate(true); }}
        >
          <PiShoppingCart aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>
      )}
      {failed && <span role="alert" className="sr-only">{t('text657')}</span>}
    </span>
  );
}
